import React from 'react';
import { GameInstance, NimGameState } from '../../../../types/types';
import useNimGamePage from '../../../../hooks/useNimGamePage';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import Input from '../../../ui/input';

/**
 * Component to display the "Nim" game page, including the rules, game details, and functionality to make a move.
 * @param gameInstance The current instance of the Nim game, including player details, game status, and remaining objects.
 * @returns A React component that shows:
 * - The rules of the Nim game.
 * - The current game details, such as players, current turn, remaining objects, and winner (if the game is over).
 * - An input field for making a move (if the game is in progress) and a submit button to finalize the move.
 */
const NimGamePage = ({ gameInstance }: { gameInstance: GameInstance<NimGameState> }) => {
  const { user, move, handleMakeMove, handleInputChange } = useNimGamePage(gameInstance);

  return (
    <div className='container mx-auto p-6 space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Rules of Nim</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='mb-2'>The game of Nim is played as follows:</p>
          <ol className='list-decimal pl-6 space-y-1 mb-4'>
            <li>The game starts with a pile of objects.</li>
            <li>Players take turns removing objects from the pile.</li>
            <li>On their turn, a player must remove 1, 2, or 3 objects from the pile.</li>
            <li>The player who removes the last object loses the game.</li>
          </ol>
          <p className='text-gray-700'>
            Think strategically and try to force your opponent into a losing position!
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Game</CardTitle>
        </CardHeader>
        <CardContent className='space-y-3'>
          <p>
            <span className='font-semibold'>Player 1:</span>{' '}
            {gameInstance.state.player1 || 'Waiting...'}
          </p>
          <p>
            <span className='font-semibold'>Player 2:</span>{' '}
            {gameInstance.state.player2 || 'Waiting...'}
          </p>
          <p>
            <span className='font-semibold'>Current Player to Move:</span>{' '}
            {gameInstance.players[gameInstance.state.moves.length % 2]}
          </p>
          <p>
            <span className='font-semibold'>Remaining Objects:</span>{' '}
            {gameInstance.state.remainingObjects}
          </p>
          {gameInstance.state.status === 'OVER' && (
            <p>
              <span className='font-semibold'>Winner:</span>{' '}
              {gameInstance.state.winners?.join(', ') || 'No winner'}
            </p>
          )}
          {gameInstance.state.status === 'IN_PROGRESS' && (
            <div className='mt-6 pt-4 border-t border-gray-200'>
              <h3 className='text-lg font-medium mb-3'>Make Your Move</h3>
              <div className='flex items-center gap-3'>
                <Input
                  type='number'
                  value={move}
                  onChange={handleInputChange}
                  min='1'
                  max='3'
                  placeholder='Enter 1-3'
                  className='w-32'
                />
                <Button
                  variant='default'
                  onClick={handleMakeMove}
                  disabled={
                    gameInstance.players[gameInstance.state.moves.length % 2] !== user.username
                  }>
                  Submit Move
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NimGamePage;
